import React from "react";
import { Activity, ActivityParticipantData, ActivityParticipantRole, PublicAmorgUser, rng, userHelper } from "../../../amorg-common";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { ItemSelectorPanel } from "../../../designSystem/compositeComponents/itemSelector/ItemSelectorPanel";
import clone from "clone";
import { Small } from "../../../designSystem/typography/Body";
import { getUsers } from "../../../api/userClientApi";
import { updateActivityParticipants } from "../../../api/activityClientApi";

type ParticipantSelectorProps = {
    activity:Activity,
    defaultAddRole: ActivityParticipantRole,
    onParticipantsUpdated:()=>void
}
export function ParticipantSelector(props:ParticipantSelectorProps):JSX.Element
{
    const [users, setUsers] = React.useState<PublicAmorgUser[]>([]);
    const [loading, setLoading] = React.useState(true);

    const singletonRoleMode = props.defaultAddRole === 'facilitator' || props.defaultAddRole === 'minuteTaker';

    React.useEffect(() => {
        const getData = async () => {
            setLoading(true);
            setUsers(await getUsers());
            setLoading(false);
        };
        getData();
    },[props.activity]);

    async function handleSelectionChanged(selectionValue:string, checked:boolean)
    {
        const updatedParticipants = clone(props.activity.participants);

        // Only one can be selected - if a new one is selected, remove the old one and add the new one
        if(singletonRoleMode)
        {
            if(selectionValue === '')
            {
                const toDemote = updatedParticipants.find(p => p.activityRole === props.defaultAddRole);
                if(toDemote)
                    toDemote.activityRole = 'participant';
            }
            else {

                // ignore checked status

                // Demote any existing participant with the role in question
                for(const p of updatedParticipants.filter(p => p.activityRole === props.defaultAddRole))
                {
                    p.activityRole = 'participant';
                }

                const reference = selectionValueToReference(selectionValue);
                if(reference.type === 'user')
                {
                    // Add new internal participant
                    updatedParticipants.push({ type:{value:'internal', userId:reference.id}, id:rng.getRandomId(),activityRole:props.defaultAddRole, name:'', role:'', email:'', initials:'' });
                }
                else {
                    // Promote existing external participant to the new role
                    const existingParticipant = updatedParticipants.find(p => p.id === reference.id);
                    if(!existingParticipant)
                        throw new Error('Invalid selection value for promoting participant: ' + selectionValue);

                    existingParticipant.activityRole = props.defaultAddRole;
                }
            }
        }
        // Many can be selected - simply toggle on/off
        else
        {
            if(checked)
            {
                const reference = selectionValueToReference(selectionValue);
                if(reference.type !== 'user')
                    throw new Error('Invalid selection value for adding participant: ' + selectionValue);

                updatedParticipants.push({ type:{value:'internal', userId:reference.id}, id:rng.getRandomId(),activityRole:props.defaultAddRole, name:'', role:'', email:'', initials:'' });
            }
            else {
                const reference = selectionValueToReference(selectionValue);
                if(reference.type !== 'participant')
                    throw new Error('Invalid selection value for removing participant: ' + selectionValue);

                updatedParticipants.splice(updatedParticipants.findIndex(p => p.id === reference.id), 1);
            }
        }

        await updateActivityParticipants(props.activity.id, updatedParticipants);
        props.onParticipantsUpdated();
    }

    function userToSelectionValue(user:PublicAmorgUser):string
    {
        return 'user@$&' + user.id;
    }

    function participantToSelectionValue(participant:ActivityParticipantData):string
    {
        return 'participant@$&' + participant.id;
    }

    function selectionValueToReference(selectionValue:string):{type:'user'|'participant', id:string}
    {
        const [type, id] = selectionValue.split('@$&');
        return { type: type as 'user'|'participant', id };
    }

    const nonselectedUsers = users.filter(u => 
            (u.permissionGroup.id === 'siteadmin' || u.permissionGroup.id === 'member')
            && !props.activity.participants.some(p => p.type.value === 'internal' && p.type.userId === u.id)
    );

    const items:{ 
        selectionValue:string,
        title:string, 
        subtitle:string, 
        selected:boolean 
    }[] = [];

    items.push(...nonselectedUsers.map(u => (
            {
                selectionValue:userToSelectionValue(u),
                title: userHelper.getPublicUserFullName(u),
                subtitle: u.permissionGroup.name,
                selected:false
            }
        ))
    );
    
   items.push(
        ...props.activity.participants.map(p => {

            let role = p.role ? `${p.role} (EKSTERN)` : 'EKSTERN';
            if(p.type.value === 'internal')
            {
                const userId = p.type.userId;
                role = users.find(u => u.id === userId)?.permissionGroup.name || '';
            }

            return {
                selectionValue:participantToSelectionValue(p),
                title: p.name,
                subtitle: role,
                type: 'participant' as ('user'|'participant'),
                selected:true
            };
        })
    );
    items.sort((a,b) => a.title.localeCompare(b.title));

    const selectMode = singletonRoleMode ? 'button-select' : 'onOff';
    if(singletonRoleMode)
    {
        items.splice(0, 0, {
            selectionValue: '',
            title: 'Ingen valgt',
            subtitle: '',
            selected: false
        });
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />
            <ItemSelectorPanel
                selectMode={selectMode}
                pageSize={10}
                items={items.map(i => ({
                    selectionValue:i.selectionValue,
                    selected:i.selected,
                    searchTexts:[i.title, i.subtitle],
                    renderComponent:
                            <Flex flexDirection="column">
                                <div>{i.title}</div>
                                <Small>{i.subtitle}</Small>
                            </Flex>
                }))}
                searchPlaceholder="Søg efter bruger"
                noItemsElement={<div>Ingen brugere fundet</div>}
                onSelectionChanged={handleSelectionChanged} />
        </>
    );
}
